// The Capability map's AREA RAIL (Capability Map v2, the "Signal" re-layout): one row per owner the
// registry carries — the display label over the module-directory value (G-197), the row's total, and a
// mini bar showing its permission mix — plus the legend that explains the bar. The rows aggregate the
// CURRENTLY BUCKET-FILTERED set so the rail always agrees with the pane beside it (G-182). Selecting a
// row narrows the pane to that owner (client view-state only). Tokens only (G-181).

import { BUCKET_META, BUCKET_ORDER, type AreaAggregate } from './capability-buckets.ts';

/** One area row: label + total + the bucket-mix bar. */
function AreaRow({
  area,
  on,
  onSelect,
}: {
  readonly area: AreaAggregate;
  readonly on: boolean;
  readonly onSelect: () => void;
}): React.JSX.Element {
  return (
    <button
      type="button"
      className={area.total === 0 ? 'cm-area empty' : 'cm-area'}
      aria-pressed={on}
      data-area={area.key}
      onClick={onSelect}
    >
      <span className="cm-area-line">
        <span className="lb">{area.label}</span>
        <span className="cnt">{area.total}</span>
      </span>
      <span className="cm-area-mix" aria-hidden="true">
        {BUCKET_ORDER.filter((key) => area.mix[key] > 0).map((key) => (
          <span key={key} data-bucket={key} style={{ flexGrow: area.mix[key] }} />
        ))}
      </span>
    </button>
  );
}

/** The area rail: the all-areas row, one row per owner, and the bar legend. */
export function CapabilityAreaRail({
  areas,
  allTotal,
  allMix,
  selected,
  onSelect,
}: {
  readonly areas: readonly AreaAggregate[];
  readonly allTotal: number;
  readonly allMix: AreaAggregate['mix'];
  readonly selected: string;
  readonly onSelect: (key: string) => void;
}): React.JSX.Element {
  const all: AreaAggregate = { key: 'all', label: 'All areas', total: allTotal, mix: allMix };
  return (
    <aside aria-label="Areas" className="cm-rail">
      <div className="cm-rail-title">Area of the business</div>
      {[all, ...areas].map((area) => (
        <AreaRow
          key={area.key}
          area={area}
          on={selected === area.key}
          onSelect={() => onSelect(area.key)}
        />
      ))}
      <div className="cm-legend">
        <span className="cm-legend-title">Reading the bars</span>
        {BUCKET_ORDER.map((key) => (
          <span key={key} className="cm-legend-row">
            <span className="swatch" data-bucket={key} aria-hidden="true" />
            {BUCKET_META[key].label}
          </span>
        ))}
      </div>
    </aside>
  );
}
